/*
 * RegionMenu: the region picker at the top right of the console, the way a
 * cloud console offers its regions.
 *
 * Each region is a separate control plane with its own console, accounts and
 * boxes, so picking one opens that region's console, which signs you in on its
 * own. Only a super admin, who runs every region, is offered the others; a
 * customer lives in one. Nothing renders when the list names no console but
 * this one.
 */
import React, { useEffect, useRef, useState } from "react";
import { FiCheck, FiChevronDown, FiGlobe } from "react-icons/fi";
import { useSession } from "./PortalGate";
import { consoleHref } from "../appView";

export default function RegionMenu({ regions = [], singleEdge = false }) {
  const session = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const here = typeof window !== "undefined" ? window.location.origin : "";

  // A click anywhere else, or Escape, closes the list.
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const offered =
    !singleEdge && session.managed && session.isSuperAdmin && regions.some((r) => r.url !== here);
  if (!offered) return null;
  const current = regions.find((r) => r.url === here);

  return (
    <div className="region-menu" ref={ref}>
      <button
        type="button"
        className="region-menu-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <FiGlobe size={14} aria-hidden="true" />
        <span>{current ? current.name : "Region"}</span>
        <FiChevronDown size={14} aria-hidden="true" className="region-menu-caret" />
      </button>
      {open && (
        <div className="region-menu-list" role="group" aria-label="Region">
          {regions.map((r) =>
            r.url === here ? (
              <span key={r.name} className="region-menu-item current" aria-current="page">
                <span className="region-menu-name">{r.name}</span>
                <span className="region-menu-host">{new URL(r.url).host}</span>
                <FiCheck size={15} aria-hidden="true" className="region-menu-check" />
              </span>
            ) : (
              <a key={r.name} className="region-menu-item" href={consoleHref(r.url)}>
                <span className="region-menu-name">{r.name}</span>
                <span className="region-menu-host">{new URL(r.url).host}</span>
              </a>
            ),
          )}
          <div className="region-menu-note">Each region has its own sign-in and its own boxes.</div>
        </div>
      )}
    </div>
  );
}
